<template>
	<div>
		<Breadcrumbs main="App" title="API Client Management" />
		<div class="container-fluid">
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>API Client Keys</h5>
								<span>Create, activate, revoke, and delete client API keys.</span>
							</div>
							<div class="d-flex gap-2">
								<button class="btn btn-outline-primary" type="button" @click="fetchClients" :disabled="loading">
									Refresh
								</button>
								<button
									v-if="$store.getters['auth/hasPermission']('apiclient:create')"
									class="btn btn-primary"
									type="button"
									@click="openCreate"
								>
									New API Client
								</button>
							</div>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">
								{{ errorMessage }}
							</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">
								{{ successMessage }}
							</div>

							<div v-if="createdApiKey" class="alert alert-warning" role="alert">
								<div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
									<div>
										<strong>New API key generated:</strong>
										<div class="mt-1 mono-key">{{ createdApiKey }}</div>
										<small class="text-muted">Copy and store this key now. It will not be shown again.</small>
									</div>
									<button class="btn btn-sm btn-outline-dark" type="button" @click="copyApiKey">
										Copy Key
									</button>
								</div>
							</div>

							<div class="mb-3 row align-items-center">
								<label for="client-search" class="col-sm-2 col-form-label">Search</label>
								<div class="col-sm-10">
									<input
										id="client-search"
										v-model="searchTerm"
										class="form-control"
										type="text"
										placeholder="Filter by client name"
									/>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered align-middle">
									<thead>
										<tr>
											<th>ID</th>
											<th>Name</th>
											<th>Status</th>
											<th>Created At</th>
											<th class="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="5" class="text-center">
												<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
												Loading clients...
											</td>
										</tr>
										<tr v-else-if="!pagedClients.length">
											<td colspan="5" class="text-center">No API clients found.</td>
										</tr>
										<tr v-for="client in pagedClients" :key="client.id">
											<td>{{ client.id }}</td>
											<td>{{ client.name }}</td>
											<td>
												<span :class="client.isActive ? 'badge badge-success' : 'badge badge-secondary'">
													{{ client.isActive ? 'Active' : 'Revoked' }}
												</span>
											</td>
											<td>{{ formatDate(client.createdAt) }}</td>
											<td class="text-center text-nowrap">
												<button
													v-if="$store.getters['auth/hasPermission']('apiclient:update') && !client.isActive"
													class="btn btn-sm btn-outline-success mr-1"
													type="button"
													@click="activateClient(client)"
													:disabled="actionLoadingId === client.id"
												>
													Activate
												</button>
												<button
													v-if="$store.getters['auth/hasPermission']('apiclient:update') && client.isActive"
													class="btn btn-sm btn-outline-warning mr-1"
													type="button"
													@click="revokeClient(client)"
													:disabled="actionLoadingId === client.id"
												>
													Revoke
												</button>
												<button
													v-if="$store.getters['auth/hasPermission']('apiclient:delete')"
													class="btn btn-sm btn-outline-danger"
													type="button"
													@click="deleteClient(client)"
													:disabled="actionLoadingId === client.id"
												>
													Delete
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div class="d-flex justify-content-between align-items-center mt-3">
								<div>
									<small class="text-muted">
										Showing {{ paginationStart }}-{{ paginationEnd }} of {{ totalItems }} clients
									</small>
								</div>
								<div class="btn-group" role="group" aria-label="Pagination">
									<button class="btn btn-outline-secondary btn-sm" type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1">
										Prev
									</button>
									<button class="btn btn-outline-secondary btn-sm" type="button" disabled>
										Page {{ currentPage }} / {{ totalPages || 1 }}
									</button>
									<button class="btn btn-outline-secondary btn-sm" type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">
										Next
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showCreateModal" class="modal-backdrop-custom" @click.self="closeCreateModal">
			<div class="modal-dialog-custom card">
				<div class="card-header d-flex justify-content-between align-items-center">
					<h5 class="mb-0">Create API Client</h5>
					<button type="button" class="close" @click="closeCreateModal"><span>&times;</span></button>
				</div>
				<div class="card-body">
					<div v-if="formError" class="alert alert-danger" role="alert">{{ formError }}</div>
					<form @submit.prevent="createClient">
						<div class="form-group">
							<label for="client-name">Client Name <span class="text-danger">*</span></label>
							<input
								id="client-name"
								v-model.trim="form.name"
								class="form-control"
								type="text"
								placeholder="e.g. Mobile App"
								required
							/>
						</div>
						<div class="d-flex justify-content-end mt-3">
							<button type="button" class="btn btn-secondary mr-2" @click="closeCreateModal">Cancel</button>
							<button type="submit" class="btn btn-primary" :disabled="saving">
								{{ saving ? 'Creating...' : 'Create Client' }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';

export default {
	name: 'ApiClientIndex',

	data() {
		return {
			clients: [],
			loading: false,
			saving: false,
			actionLoadingId: null,
			errorMessage: '',
			successMessage: '',
			createdApiKey: '',
			searchTerm: '',
			showCreateModal: false,
			form: {
				name: '',
			},
			formError: '',
			currentPage: 1,
			pageSize: 20,
			totalItems: 0,
			totalPages: 1,
		};
	},

	computed: {
		filteredClients() {
			if (!this.searchTerm.trim()) return this.clients;
			const q = this.searchTerm.trim().toLowerCase();
			return this.clients.filter((c) => (c.name || '').toLowerCase().includes(q));
		},
		pagedClients() {
			const start = (this.currentPage - 1) * this.pageSize;
			return this.filteredClients.slice(start, start + this.pageSize);
		},
		paginationStart() {
			if (this.totalItems === 0) return 0;
			return (this.currentPage - 1) * this.pageSize + 1;
		},
		paginationEnd() {
			if (this.totalItems === 0) return 0;
			return Math.min(this.currentPage * this.pageSize, this.totalItems);
		},
	},

	watch: {
		searchTerm() {
			this.currentPage = 1;
			this.updatePagination();
		},
	},

	mounted() {
		this.$store.dispatch('auth/loadPermissions');
		if (!this.$store.getters['auth/hasPermission']('apiclient:read')) {
			this.errorMessage = 'You do not have permission to view API clients.';
			return;
		}
		this.fetchClients();
	},

	methods: {
		authHeaders() {
			return {
				Authorization: `Bearer ${localStorage.getItem('token')}`,
				'Content-Type': 'application/json',
			};
		},

		showSuccess(message) {
			this.successMessage = message;
			this.errorMessage = '';
			setTimeout(() => {
				this.successMessage = '';
			}, 4000);
		},

		showError(message) {
			this.errorMessage = message;
			this.successMessage = '';
		},

		updatePagination() {
			this.totalItems = this.filteredClients.length;
			this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
			if (this.currentPage > this.totalPages) {
				this.currentPage = this.totalPages;
			}
		},

		goToPage(page) {
			if (page < 1 || page > this.totalPages) return;
			this.currentPage = page;
		},

		formatDate(value) {
			if (!value) return '-';
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) return '-';
			return date.toLocaleString();
		},

		openCreate() {
			this.form = { name: '' };
			this.formError = '';
			this.showCreateModal = true;
		},

		closeCreateModal() {
			this.showCreateModal = false;
		},

		async fetchClients() {
			this.loading = true;
			this.errorMessage = '';
			try {
				const response = await fetch(`${API_BASE}/api/api-clients`, {
					method: 'GET',
					headers: this.authHeaders(),
				});

				if (!response.ok) {
					let message = `Failed to load API clients (${response.status})`;
					try {
						const errorData = await response.json();
						message = errorData.message || errorData.title || message;
					} catch {
						// ignore parse error
					}
					throw new Error(message);
				}

				const data = await response.json();
				this.clients = Array.isArray(data) ? data : (data.items || data.data || []);
				this.updatePagination();
			} catch (error) {
				this.showError(error.message || 'Failed to load API clients.');
			} finally {
				this.loading = false;
			}
		},

		async createClient() {
			this.formError = '';
			this.saving = true;
			try {
				const response = await fetch(`${API_BASE}/api/api-clients`, {
					method: 'POST',
					headers: this.authHeaders(),
					body: JSON.stringify({ name: this.form.name }),
				});

				if (!response.ok) {
					let message = `Create failed (${response.status})`;
					try {
						const errorData = await response.json();
						message = errorData.message || errorData.title || message;
					} catch {
						// ignore parse error
					}
					this.formError = message;
					return;
				}

				const created = await response.json();
				this.createdApiKey = created.apiKey || '';
				this.closeCreateModal();
				this.showSuccess(`API client "${created.name || this.form.name}" created.`);
				await this.fetchClients();
			} catch (error) {
				this.formError = error.message || 'Create request failed.';
			} finally {
				this.saving = false;
			}
		},

		async revokeClient(client) {
			this.actionLoadingId = client.id;
			try {
				const response = await fetch(`${API_BASE}/api/api-clients/${client.id}/revoke`, {
					method: 'POST',
					headers: this.authHeaders(),
				});

				if (!response.ok) {
					let message = `Revoke failed (${response.status})`;
					try {
						const errorData = await response.json();
						message = errorData.message || errorData.title || message;
					} catch {
						// ignore parse error
					}
					throw new Error(message);
				}

				this.showSuccess(`Client "${client.name}" revoked.`);
				await this.fetchClients();
			} catch (error) {
				this.showError(error.message || 'Failed to revoke client.');
			} finally {
				this.actionLoadingId = null;
			}
		},

		async activateClient(client) {
			this.actionLoadingId = client.id;
			try {
				const response = await fetch(`${API_BASE}/api/api-clients/${client.id}/activate`, {
					method: 'POST',
					headers: this.authHeaders(),
				});

				if (!response.ok) {
					let message = `Activate failed (${response.status})`;
					try {
						const errorData = await response.json();
						message = errorData.message || errorData.title || message;
					} catch {
						// ignore parse error
					}
					throw new Error(message);
				}

				this.showSuccess(`Client "${client.name}" activated.`);
				await this.fetchClients();
			} catch (error) {
				this.showError(error.message || 'Failed to activate client.');
			} finally {
				this.actionLoadingId = null;
			}
		},

		async deleteClient(client) {
			if (!window.confirm(`Delete API client "${client.name}"? This cannot be undone.`)) return;

			this.actionLoadingId = client.id;
			try {
				const response = await fetch(`${API_BASE}/api/api-clients/${client.id}`, {
					method: 'DELETE',
					headers: this.authHeaders(),
				});

				if (!response.ok) {
					let message = `Delete failed (${response.status})`;
					try {
						const errorData = await response.json();
						message = errorData.message || errorData.title || message;
					} catch {
						// ignore parse error
					}
					throw new Error(message);
				}

				if ((this.currentPage - 1) * this.pageSize >= Math.max(this.totalItems - 1, 0) && this.currentPage > 1) {
					this.currentPage -= 1;
				}
				this.showSuccess(`Client "${client.name}" deleted.`);
				await this.fetchClients();
			} catch (error) {
				this.showError(error.message || 'Failed to delete client.');
			} finally {
				this.actionLoadingId = null;
			}
		},

		async copyApiKey() {
			if (!this.createdApiKey) return;
			try {
				await navigator.clipboard.writeText(this.createdApiKey);
				this.showSuccess('API key copied to clipboard.');
			} catch {
				this.showError('Failed to copy API key. Please copy it manually.');
			}
		},
	},
};
</script>

<style scoped>
.mono-key {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace;
	word-break: break-all;
}
</style>
